import { EventEmitter } from 'eventemitter3';

export type AudioUrlPlayerEventMap = {
  'playback-started': () => void;
  'playback-complete': () => void;
  'playback-error': (error: Error) => void;
  'time-update': (currentTime: number, duration: number) => void;
  'loading': (isLoading: boolean) => void;
};

/**
 * Service for playing audio from URLs using HTML5 Audio element
 * Provides an alternative to PCM streaming for pre-generated audio files
 */
export class AudioUrlPlayer extends EventEmitter<AudioUrlPlayerEventMap> {
  private audioElement: HTMLAudioElement | null = null;
  private currentUrl: string | null = null;
  private isDestroyed: boolean = false;

  constructor() {
    super();
  }

  /**
   * Play audio from a URL
   * @param url The URL of the audio file to play
   * @param startTime Optional start time in seconds
   */
  public async play(url: string, startTime: number = 0): Promise<void> {
    if (this.isDestroyed) {
      throw new Error('AudioUrlPlayer has been destroyed');
    }

    try {
      // Stop current audio if playing
      this.stop();

      // Create new audio element
      this.audioElement = new Audio(url);
      this.currentUrl = url;

      // Set up event handlers
      this.setupEventHandlers();

      // Set start time if provided
      if (startTime > 0) {
        this.audioElement.currentTime = startTime;
      }

      // Start loading
      this.emit('loading', true);
      
      // Wait for audio to be ready
      await new Promise<void>((resolve, reject) => {
        if (!this.audioElement) {
          reject(new Error('Audio element not initialized'));
          return;
        }

        const handleCanPlay = () => {
          this.emit('loading', false);
          resolve();
        };

        const handleError = () => {
          this.emit('loading', false);
          reject(new Error(`Failed to load audio from ${url}`));
        };

        this.audioElement.addEventListener('canplay', handleCanPlay, { once: true });
        this.audioElement.addEventListener('error', handleError, { once: true });
        
        // Start loading
        this.audioElement.load();
      });

      // Play the audio
      await this.audioElement.play();
      this.emit('playback-started');
      
    } catch (error) {
      const err = error instanceof Error ? error : new Error('Failed to play audio');
      this.emit('playback-error', err);
      throw err;
    }
  }

  /**
   * Pause the current audio playback
   */
  public pause(): void {
    if (this.audioElement && !this.audioElement.paused) {
      this.audioElement.pause();
    }
  }

  /**
   * Resume the current audio playback
   */
  public resume(): void {
    if (this.audioElement && this.audioElement.paused) {
      this.audioElement.play().catch(error => {
        this.emit('playback-error', new Error(`Failed to resume playback: ${error.message}`));
      });
    }
  }

  /**
   * Stop the current audio playback and cleanup
   */
  public stop(): void {
    if (this.audioElement) {
      this.audioElement.pause();
      this.audioElement.src = '';
      this.removeEventHandlers();
      this.audioElement = null;
      this.currentUrl = null;
    }
  }

  /**
   * Get the current playback time in seconds
   */
  public getCurrentTime(): number {
    return this.audioElement?.currentTime ?? 0;
  }

  /**
   * Get the total duration in seconds
   */
  public getDuration(): number {
    return this.audioElement?.duration ?? 0;
  }

  /**
   * Set the playback volume (0.0 to 1.0)
   */
  public setVolume(volume: number): void {
    if (this.audioElement) {
      this.audioElement.volume = Math.max(0, Math.min(1, volume));
    }
  }

  /**
   * Check if audio is currently playing
   */
  public isPlaying(): boolean {
    return this.audioElement != null && !this.audioElement.paused;
  }

  /**
   * Get the current URL being played
   */
  public getCurrentUrl(): string | null {
    return this.currentUrl;
  }

  /**
   * Destroy the player and cleanup resources
   */
  public destroy(): void {
    this.stop();
    this.removeAllListeners();
    this.isDestroyed = true;
  }

  private setupEventHandlers(): void {
    if (!this.audioElement) return;

    // Playback complete
    this.audioElement.addEventListener('ended', this.handleEnded);
    
    // Time updates for progress tracking
    this.audioElement.addEventListener('timeupdate', this.handleTimeUpdate);
    
    // Error handling
    this.audioElement.addEventListener('error', this.handleError);
    
    // Loading events
    this.audioElement.addEventListener('loadstart', this.handleLoadStart);
    this.audioElement.addEventListener('loadeddata', this.handleLoadComplete);
  }

  private removeEventHandlers(): void {
    if (!this.audioElement) return;

    this.audioElement.removeEventListener('ended', this.handleEnded);
    this.audioElement.removeEventListener('timeupdate', this.handleTimeUpdate);
    this.audioElement.removeEventListener('error', this.handleError);
    this.audioElement.removeEventListener('loadstart', this.handleLoadStart);
    this.audioElement.removeEventListener('loadeddata', this.handleLoadComplete);
  }

  private handleEnded = (): void => {
    this.emit('playback-complete');
    this.stop();
  };

  private handleTimeUpdate = (): void => {
    if (this.audioElement) {
      this.emit('time-update', this.audioElement.currentTime, this.audioElement.duration);
    }
  };

  private handleError = (event: Event): void => {
    const target = event.target as HTMLAudioElement;
    const error = target?.error;
    let message = 'Unknown audio playback error';
    
    if (error) {
      switch (error.code) {
        case error.MEDIA_ERR_ABORTED:
          message = 'Audio playback aborted';
          break;
        case error.MEDIA_ERR_NETWORK:
          message = 'Network error while loading audio';
          break;
        case error.MEDIA_ERR_DECODE:
          message = 'Audio decoding error';
          break;
        case error.MEDIA_ERR_SRC_NOT_SUPPORTED:
          message = 'Audio format not supported';
          break;
      }
    }
    
    this.emit('playback-error', new Error(message));
  };

  private handleLoadStart = (): void => {
    this.emit('loading', true);
  };

  private handleLoadComplete = (): void => {
    this.emit('loading', false);
  };
}